import { useCallback, useMemo } from "react";
import { Message, MessagesState } from "@/lib/core/crm/types";
import type { UseMutationResult } from "@tanstack/react-query";

// Action type constants
const ACTION_TYPES = {
  MARK_READ: "mark_as_read",
  MARK_UNREAD: "mark_as_unread",
  DELETE: "delete",
  ARCHIVE: "archive",
} as const;

// Message action parameters type - matches MessageActionVariables from messages.ts
interface MessageActionParams {
  provider: string;
  messageId: string;
  connectionId: string;
  action: "mark_as_read" | "mark_as_unread" | "delete" | "archive";
  payload?: unknown;
}

// Helper: Calculate unread count for messages
function calculateUnreadCount(messages: Message[]): number {
  return messages.filter((msg) => !msg.isRead).length;
}

// Helper: Sort messages by received date (newest first)
function sortMessagesByDate(messages: Message[]): Message[] {
  return [...messages].sort(
    (a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime()
  );
}

// Helper: Create state updater for removing a message
function createRemoveMessageUpdater(inboxId: string, provider: string, messageId: string) {
  return (prev: MessagesState) => {
    const messages = prev.messagesByInbox[inboxId]?.[provider] || [];
    const updatedMessages = messages.filter((msg) => msg.id !== messageId);

    return {
      ...prev,
      messagesByInbox: {
        ...prev.messagesByInbox,
        [inboxId]: {
          ...prev.messagesByInbox[inboxId],
          [provider]: updatedMessages,
        },
      },
      unreadCounts: {
        ...prev.unreadCounts,
        [inboxId]: {
          ...prev.unreadCounts[inboxId],
          [provider]: calculateUnreadCount(updatedMessages),
        },
      },
    };
  };
}

// Helper: Create state updater for restoring a message
function createRestoreMessageUpdater(inboxId: string, provider: string, messageToRestore: Message) {
  return (prev: MessagesState) => {
    const messages = prev.messagesByInbox[inboxId]?.[provider] || [];
    const restoredMessages = sortMessagesByDate([...messages, messageToRestore]);

    return {
      ...prev,
      messagesByInbox: {
        ...prev.messagesByInbox,
        [inboxId]: {
          ...prev.messagesByInbox[inboxId],
          [provider]: restoredMessages,
        },
      },
      unreadCounts: {
        ...prev.unreadCounts,
        [inboxId]: {
          ...prev.unreadCounts[inboxId],
          [provider]: calculateUnreadCount(restoredMessages),
        },
      },
    };
  };
}

// Helper: Perform optimistic action with rollback
async function performOptimisticAction(
  optimisticUpdater: (prev: MessagesState) => MessagesState,
  rollbackUpdater: (prev: MessagesState) => MessagesState,
  mutationParams: MessageActionParams,
  setState: React.Dispatch<React.SetStateAction<MessagesState>>,
  messageActionMutation: UseMutationResult<void, Error, MessageActionParams>
) {
  setState(optimisticUpdater);
  try {
    await messageActionMutation.mutateAsync(mutationParams);
  } catch (error) {
    setState(rollbackUpdater);
    throw error;
  }
}

/**
 * Creates action functions for managing messages
 * @param state - Current messages state
 * @param setState - State setter function
 * @param updateMessage - Function to update a single message
 * @param messageActionMutation - React Query mutation for message actions
 * @returns Object containing all action functions
 */
export function useActions(
  state: MessagesState,
  setState: React.Dispatch<React.SetStateAction<MessagesState>>,
  updateMessage: (
    inboxId: string,
    provider: string,
    messageId: string,
    updates: Partial<Message>
  ) => void,
  messageActionMutation: UseMutationResult<any, Error, any>
) {
  /**
   * Factory for creating read/unread toggle actions
   * Eliminates duplication between markAsRead and markAsUnread
   */
  const createToggleReadAction = (
    isRead: boolean,
    actionType: (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES]
  ) =>
    useCallback(
      async (inboxId: string, provider: string, messageId: string) => {
        // Optimistic update
        updateMessage(inboxId, provider, messageId, { isRead });

        try {
          await messageActionMutation.mutateAsync({
            provider,
            messageId,
            connectionId: inboxId,
            action: actionType,
          });
        } catch (error) {
          // Revert optimistic update on error
          updateMessage(inboxId, provider, messageId, { isRead: !isRead });
          throw error;
        }
      },
      [updateMessage, messageActionMutation]
    );

  /**
   * Factory for creating remove/archive actions
   * Consolidates deleteMessage and archiveMessage logic
   */
  const createRemoveMessageAction = (
    actionType: (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES]
  ) =>
    useCallback(
      async (inboxId: string, provider: string, messageId: string) => {
        // Store the original message for rollback
        const originalMessages = state.messagesByInbox[inboxId]?.[provider] || [];
        const messageToRestore = originalMessages.find((msg) => msg.id === messageId);

        if (!messageToRestore) return;

        // Create state updaters
        const optimisticUpdater = createRemoveMessageUpdater(inboxId, provider, messageId);
        const rollbackUpdater = createRestoreMessageUpdater(inboxId, provider, messageToRestore);

        // Perform optimistic action with rollback
        await performOptimisticAction(
          optimisticUpdater,
          rollbackUpdater,
          {
            provider,
            messageId,
            connectionId: inboxId,
            action: actionType,
          },
          setState,
          messageActionMutation
        );
      },
      [state.messagesByInbox, messageActionMutation]
    );

  // Mark message as read
  const markAsRead = createToggleReadAction(true, ACTION_TYPES.MARK_READ);

  // Mark message as unread
  const markAsUnread = createToggleReadAction(false, ACTION_TYPES.MARK_UNREAD);

  // Delete message
  const deleteMessage = createRemoveMessageAction(ACTION_TYPES.DELETE);

  // Archive message
  const archiveMessage = createRemoveMessageAction(ACTION_TYPES.ARCHIVE);

  // Stable slice object — a fresh object literal here would defeat the
  // provider's useMemo. See docs/engineering-rules/MEMO_STABLE_PROPS.md.
  return useMemo(
    () => ({
      markAsRead,
      markAsUnread,
      deleteMessage,
      archiveMessage,
    }),
    [markAsRead, markAsUnread, deleteMessage, archiveMessage]
  );
}
